import type {
    CSSProperties,
    ReactNode
} from "react";
import type {
    AnatomySampleMap,
    DocsTranslate
} from "../types";
import type {
    IDataTableColumn,
    ChartConfig,
    ITreeNode
} from "ncore-ui-kit";
import {
    DataTable,
    LineChart,
    AreaChart,
    PieChart,
    Timeline,
    Carousel,
    TreeView,
    StatTile,
    BarChart,
    RowCard,
    Button,
    Empty,
    Table,
    Chart,
    Card,
    Text
} from "ncore-ui-kit";
import {
    ChevronRightIcon,
    CheckIcon,
    BellIcon,
    PlusIcon,
    HomeIcon,
    FileIcon,
    UserIcon,
    StarIcon
} from "../icons";
import {
    shapeConfigs
} from "../sampleTokens";

type SampleRow = {
    status: string;
    amount: string;
    name: string;
    id: string;
};

const COLUMN_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex"
};

const OFFSCREEN_SLIDE_STYLE = {
    opacity: 0
};

const SAMPLE_TIME_FIRST = new Date(2026, 9, 24, 12, 32);

const SAMPLE_TIME_SECOND = new Date(2026, 9, 24, 11, 5);

const SAMPLE_TIME_THIRD = new Date(2026, 9, 24, 9, 48);

const getRows = (t: DocsTranslate): Array<SampleRow> => [
    {
        status: t("sample.statusActive"),
        amount: t("sample.amount1"),
        name: t("sample.option1"),
        id: "row1"
    },
    {
        status: t("sample.statusPending"),
        amount: t("sample.amount2"),
        name: t("sample.option2"),
        id: "row2"
    },
    {
        status: t("sample.statusActive"),
        amount: t("sample.amount1"),
        name: t("sample.option3"),
        id: "row3"
    }
];

const getColumns = (t: DocsTranslate): Array<IDataTableColumn<SampleRow>> => [
    {
        header: t("sample.columnName"),
        accessor: (row) => row.name,
        isResizable: false,
        isSortable: true,
        id: "name"
    },
    {
        header: t("sample.columnStatus"),
        accessor: (row) => row.status,
        isResizable: false,
        id: "status"
    },
    {
        header: t("sample.columnAmount"),
        accessor: (row) => row.amount,
        isResizable: false,
        isSortable: true,
        isNumeric: true,
        id: "amount"
    }
];

const getChartConfig = (t: DocsTranslate): ChartConfig => ({
    revenue: {
        title: t("sample.series1"),
        colorIndex: 1
    },
    spend: {
        title: t("sample.series2"),
        colorIndex: 2
    }
});

const getPieConfig = (t: DocsTranslate): ChartConfig => ({
    option1: {
        title: t("sample.option1"),
        colorIndex: 1
    },
    option2: {
        title: t("sample.option2"),
        colorIndex: 2
    },
    option3: {
        title: t("sample.option3"),
        colorIndex: 3
    }
});

const getChartData = (t: DocsTranslate) => [
    {
        month: t("sample.month1"),
        revenue: 120,
        spend: 80
    },
    {
        month: t("sample.month2"),
        revenue: 160,
        spend: 95
    },
    {
        month: t("sample.month3"),
        revenue: 140,
        spend: 110
    },
    {
        month: t("sample.month4"),
        revenue: 190,
        spend: 105
    }
];

const PIE_DATA = [
    {
        option: "option1",
        value: 540
    },
    {
        option: "option2",
        value: 320
    },
    {
        option: "option3",
        value: 140
    }
];

const getTreeNodes = (t: DocsTranslate): Array<ITreeNode> => [
    {
        children: [
            {
                title: t("sample.fileName"),
                meta: t("sample.fileSize"),
                icon: FileIcon,
                key: "file"
            },
            {
                title: t("sample.page"),
                icon: FileIcon,
                key: "page"
            }
        ],
        title: t("sample.home"),
        icon: HomeIcon,
        key: "home"
    },
    {
        title: t("sample.library"),
        icon: StarIcon,
        key: "library"
    }
];

const renderChart = (t: DocsTranslate, config: ChartConfig, chart: ReactNode, note?: string) => <Chart
    description={t("sample.subtitle")}
    title={t("sample.title")}
    config={config}
    note={note}
>
    {chart}
</Chart>;

const renderTreeView = (t: DocsTranslate, expandedKeys: Array<string>) => <TreeView
    aria-label={t("sample.title")}
    expandedKeys={expandedKeys}
    nodes={getTreeNodes(t)}
    selectionMode="single"
    isGuideVisible={true}
    variant="outline"
    selectedKeys={[
        "library"
    ]}
/>;

const renderSlide = (t: DocsTranslate, title: string, style?: typeof OFFSCREEN_SLIDE_STYLE) => <Carousel.Item
    label={title}
    style={style}
>
    <Card
        size="small"
    >
        <Card.Header>
            <Card.Title>
                {title}
            </Card.Title>
            <Card.Description>
                {t("sample.subtitle")}
            </Card.Description>
        </Card.Header>
        <Card.Content>
            <Text>
                {t("sample.body")}
            </Text>
        </Card.Content>
    </Card>
</Carousel.Item>;

export const dataSamples: AnatomySampleMap = {
    Carousel: (t) => ({
        node: <Carousel
            aria-label={t("sample.title")}
            controlsPlacement="outside"
            isAutoplayEnabled={false}
            indicator="dots"
        >
            <Carousel.Content>
                {renderSlide(t, t("sample.option1"))}
                {renderSlide(t, t("sample.option2"), OFFSCREEN_SLIDE_STYLE)}
                {renderSlide(t, t("sample.option3"), OFFSCREEN_SLIDE_STYLE)}
            </Carousel.Content>
        </Carousel>,
        width: shapeConfigs.drawerWidth,
        maxScale: 2
    }),
    DataTable: (t) => ({
        node: <DataTable
            caption={t("sample.title")}
            getRowId={(row) => row.id}
            columns={getColumns(t)}
            layoutBehaviour="table"
            data={getRows(t)}
        >
            <DataTable.Toolbar>
                <DataTable.Search
                    accessibilityLabel={t("sample.search")}
                    placeholder={t("sample.search")}
                />
                <DataTable.Spacer />
                <DataTable.ViewOptions />
            </DataTable.Toolbar>
            <DataTable.Content />
            <DataTable.Pagination />
        </DataTable>,
        width: shapeConfigs.dialogMaxWidth,
        maxScale: 2
    }),
    AreaChart: (t) => ({
        node: renderChart(t, getChartConfig(t), <AreaChart
            stackBehaviour="stacked"
            data={getChartData(t)}
            categoryKey="month"
            plotSize="small"
        />),
        width: shapeConfigs.dialogMaxWidth,
        maxScale: 2
    }),
    LineChart: (t) => ({
        node: renderChart(t, getChartConfig(t), <LineChart
            data={getChartData(t)}
            categoryKey="month"
            isShowDots={true}
            plotSize="small"
        />),
        width: shapeConfigs.dialogMaxWidth,
        maxScale: 2
    }),
    BarChart: (t) => ({
        node: renderChart(t, getChartConfig(t), <BarChart
            data={getChartData(t)}
            categoryKey="month"
            plotSize="small"
        />),
        width: shapeConfigs.dialogMaxWidth,
        maxScale: 2
    }),
    Chart: (t) => ({
        node: renderChart(t, getChartConfig(t), <BarChart
            data={getChartData(t)}
            categoryKey="month"
            plotSize="small"
        />, t("sample.hint")),
        width: shapeConfigs.dialogMaxWidth,
        maxScale: 2
    }),
    RowCard: (t) => ({
        node: <div
            style={COLUMN_STYLE}
        >
            <RowCard
                rightSubTitle={t("sample.metricChange")}
                rightTitle={t("sample.amount1")}
                subTitle={t("sample.subtitle")}
                rightIcon={ChevronRightIcon}
                backgroundColor="default"
                onPress={() => undefined}
                title={t("sample.title")}
                icon={BellIcon}
            />
        </div>,
        width: shapeConfigs.drawerWidth,
        maxScale: 3
    }),
    PieChart: (t) => ({
        node: renderChart(t, getPieConfig(t), <PieChart
            categoryKey="option"
            plotSize="small"
            valueKey="value"
            data={PIE_DATA}
        />),
        width: shapeConfigs.dialogMaxWidth,
        maxScale: 2
    }),
    Table: (t) => ({
        node: <Table
            caption={t("sample.title")}
        >
            <Table.Header>
                <Table.Row>
                    <Table.Head>
                        {t("sample.columnName")}
                    </Table.Head>
                    <Table.Head>
                        {t("sample.columnStatus")}
                    </Table.Head>
                    <Table.Head
                        isNumeric={true}
                    >
                        {t("sample.columnAmount")}
                    </Table.Head>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                <Table.Row>
                    <Table.Cell>
                        {t("sample.option1")}
                    </Table.Cell>
                    <Table.Cell>
                        {t("sample.statusActive")}
                    </Table.Cell>
                    <Table.Cell
                        isNumeric={true}
                    >
                        {t("sample.amount1")}
                    </Table.Cell>
                </Table.Row>
                <Table.Row>
                    <Table.Cell>
                        {t("sample.option2")}
                    </Table.Cell>
                    <Table.Cell>
                        {t("sample.statusPending")}
                    </Table.Cell>
                    <Table.Cell
                        isNumeric={true}
                    >
                        {t("sample.amount2")}
                    </Table.Cell>
                </Table.Row>
            </Table.Body>
            <Table.Footer>
                <Table.Row>
                    <Table.Cell
                        colSpan={2}
                    >
                        {t("sample.label")}
                    </Table.Cell>
                    <Table.Cell
                        isNumeric={true}
                    >
                        {t("sample.amount1")}
                    </Table.Cell>
                </Table.Row>
            </Table.Footer>
        </Table>,
        width: shapeConfigs.dialogMaxWidth,
        maxScale: 3
    }),
    Timeline: (t) => ({
        node: <Timeline
            accessibilityLabel={t("sample.title")}
            timeBehaviour="absolute"
        >
            <Timeline.Item
                description={t("sample.message")}
                title={t("sample.notification")}
                avatar={{
                    title: t("sample.name")
                }}
                time={SAMPLE_TIME_FIRST}
            />
            <Timeline.Item
                title={t("sample.description")}
                time={SAMPLE_TIME_SECOND}
                icon={CheckIcon}
                type="success"
            />
            <Timeline.Item
                title={t("sample.step3")}
                time={SAMPLE_TIME_THIRD}
                isDot={true}
            />
        </Timeline>,
        width: shapeConfigs.dialogMaxWidth,
        maxScale: 2
    }),
    TreeView: (t) => ({
        states: [
            {
                label: t("sample.stateClosed"),
                node: renderTreeView(t, [])
            },
            {
                label: t("sample.stateOpen"),
                node: renderTreeView(t, [
                    "home"
                ])
            }
        ],
        width: shapeConfigs.drawerWidth,
        maxScale: 2
    }),
    Empty: (t) => ({
        node: <Empty
            variant="card"
        >
            <Empty.Header>
                <Empty.Media
                    icon={PlusIcon}
                />
                <Empty.Title>
                    {t("sample.empty")}
                </Empty.Title>
                <Empty.Description>
                    {t("sample.body")}
                </Empty.Description>
            </Empty.Header>
            <Empty.Content>
                <Button
                    title={t("sample.action")}
                    onPress={() => undefined}
                    icon={PlusIcon}
                />
            </Empty.Content>
        </Empty>,
        width: shapeConfigs.drawerWidth,
        maxScale: 2
    }),
    StatTile: (t) => ({
        node: <StatTile
            isTrendFlush={true}
            trend={[
                42,
                45,
                41,
                52,
                49,
                57
            ]}
        >
            <StatTile.Title
                icon={UserIcon}
            >
                {t("sample.metric")}
            </StatTile.Title>
            <StatTile.Value>
                {t("sample.metricValue")}
            </StatTile.Value>
            <StatTile.Delta
                description={t("sample.hint")}
                value={0.12}
            />
        </StatTile>,
        width: shapeConfigs.drawerWidth,
        maxScale: 3
    })
};
